import React, { useState, useEffect, useRef, useCallback } from 'react';
import { onGameEvent, emitGameEvent } from '../../core/GameEvents';
import { StationLobby } from './StationLobby';
import { StationGlobe } from './StationGlobe';
import { Shipyard } from './Shipyard';
import { MarketShop } from './MarketShop';
import { Bank } from './Bank';
import { Bounties } from './Bounties';
import { RepairBay } from './RepairBay';
import { DroneBay } from './DroneBay';
import { Crafting } from './Crafting';
import { PrivateStationPanel } from './PrivateStationPanel';
import { PrivateStationBank } from './PrivateStationBank';
import { CorporationPanel } from './CorporationPanel';
import { audioManager } from '../../audio/AudioManager';
import { networkManager } from '../../systems/NetworkManager';
import { PALETTE } from './stationTheme';

export type StationView = 'lobby' | 'shipyard' | 'market' | 'bank' | 'bounties' | 'repair' | 'dronebay' | 'crafting' | 'private_station' | 'private_bank' | 'corporation';

interface StationUIProps {
  stationId: string;
  stationName: string;
  onUndock: () => void;
}

const DEFAULT_RIGHT_PCT = 65;
const MIN_RIGHT_PCT = 20;
const MAX_RIGHT_PCT = 70;

export function StationUI({ stationId, stationName, onUndock }: StationUIProps) {
  const [currentView, setCurrentView] = useState<StationView>('lobby');
  const [credits, setCredits] = useState(0);
  const [rightPct, setRightPct] = useState(DEFAULT_RIGHT_PCT);
  const splitRef = useRef<HTMLDivElement>(null);
  const draggingRef = useRef(false);

  // Detect whether the docked station is a player-owned private station
  const isPrivate = (() => {
    const room: any = (networkManager as any).room;
    return room?.state?.privateStations?.get?.(stationId) != null;
  })();

  // Detect whether the "station" we docked at is actually a drone settlement (market only).
  const isSettlement = (() => {
    const room: any = (networkManager as any).room;
    return room?.state?.settlements?.get?.(stationId) != null;
  })();

  useEffect(() => {
    audioManager.playMusic('station');
    return () => {};
  }, []);

  useEffect(() => {
    const unsub = onGameEvent('playerStateUpdate', (state) => {
      setCredits(state.credits);
    });
    return unsub;
  }, []);

  // ============ Splitter drag logic ============
  const onSplitterDown = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    draggingRef.current = true;
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  }, []);

  const onSplitterMove = useCallback((e: React.PointerEvent) => {
    if (!draggingRef.current || !splitRef.current) return;
    const rect = splitRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const pct = 100 - (x / rect.width) * 100;
    setRightPct(Math.max(MIN_RIGHT_PCT, Math.min(MAX_RIGHT_PCT, pct)));
  }, []);

  const onSplitterUp = useCallback(() => {
    draggingRef.current = false;
  }, []);

  const handleNavigate = (view: StationView) => {
    setCurrentView(view);
  };

  const handleBack = () => {
    setCurrentView('lobby');
  };

  const renderRightPanel = () => {
    switch (currentView) {
      case 'lobby':
        return (
          <StationLobby
            stationId={stationId}
            stationName={stationName}
            onNavigate={handleNavigate}
            isPrivate={isPrivate}
            isSettlement={isSettlement}
          />
        );
      case 'shipyard':
        return <Shipyard stationId={stationId} onBack={handleBack} />;
      case 'market':
        return <MarketShop stationId={stationId} onBack={handleBack} />;
      case 'bank':
        return <Bank stationId={stationId} onBack={handleBack} />;
      case 'bounties':
        return <Bounties stationId={stationId} onBack={handleBack} />;
      case 'repair':
        return <RepairBay stationId={stationId} onBack={handleBack} />;
      case 'dronebay':
        return <DroneBay stationId={stationId} onBack={handleBack} />;
      case 'crafting':
        return <Crafting stationId={stationId} onBack={handleBack} />;
      case 'private_station':
        return <PrivateStationPanel stationId={stationId} onBack={handleBack} />;
      case 'private_bank':
        return <PrivateStationBank stationId={stationId} onBack={handleBack} />;
      case 'corporation':
        return <CorporationPanel stationId={stationId} onBack={handleBack} />;
      default:
        return null;
    }
  };

  return (
    <div style={containerStyle}>
      {/* Header */}
      <div style={topBarStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <span style={stationNameStyle}>{stationName.toUpperCase()}</span>
          <button
            style={undockBtnStyle}
            onClick={onUndock}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = PALETTE.ORANGE;
              e.currentTarget.style.color = PALETTE.DARK;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = PALETTE.ORANGE;
            }}
          >
            UNDOCK
          </button>
        </div>
        <span style={creditsStyle}>{credits.toLocaleString()} CR</span>
      </div>

      {/* Split layout — always visible */}
      <div ref={splitRef} style={splitContainerStyle}>
        {/* Left: 3D station model (NPC/private stations only). A settlement looks totally
            different in space (a rogue-drone cluster), so the station globe would be
            confusing — show a themed placeholder instead. */}
        <div style={{ ...globePanelStyle, width: `${100 - rightPct}%` }}>
          {isSettlement ? (
            <div style={{
              height: '100%', width: '100%', display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center', padding: 24, boxSizing: 'border-box',
            }}>
              <div style={{ fontSize: 72, lineHeight: 1, color: '#9b5cff' }}>⚠</div>
              <div style={{ fontSize: 18, letterSpacing: 3, marginTop: 18, color: '#c9a6ff' }}>ROGUE SETTLEMENT</div>
              <div style={{ fontSize: 13, opacity: 0.65, marginTop: 10, maxWidth: 340, textAlign: 'center', lineHeight: 1.5, color: '#a98fd0' }}>
                Unsanctioned drone market — no safe zone. Trade fast and undock if its fighters turn on you.
              </div>
            </div>
          ) : (
            <StationGlobe />
          )}
        </div>

        {/* Draggable splitter */}
        <div
          style={splitterStyle}
          onPointerDown={onSplitterDown}
          onPointerMove={onSplitterMove}
          onPointerUp={onSplitterUp}
        >
          <div style={splitterLineStyle} />
        </div>

        {/* Right: all station content lives here */}
        <div style={{ ...optionsPanelStyle, width: `${rightPct}%` }}>
          {renderRightPanel()}
        </div>
      </div>
    </div>
  );
}

export default StationUI;

// ==================== STYLES ====================

const containerStyle: React.CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundColor: PALETTE.DARK,
  fontFamily: "'JetBrains Mono', monospace",
  color: PALETTE.LIGHT,
  display: 'flex',
  flexDirection: 'column',
};

const topBarStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '12px 20px',
  borderBottom: `1px solid ${PALETTE.BORDER}`,
  flexShrink: 0,
};

const stationNameStyle: React.CSSProperties = {
  fontSize: '11px',
  fontWeight: 600,
  color: PALETTE.LIGHT,
  letterSpacing: '0.1em',
};

const creditsStyle: React.CSSProperties = {
  fontSize: '11px',
  color: PALETTE.TEAL,
  fontWeight: 600,
};

const splitContainerStyle: React.CSSProperties = {
  flex: 1,
  display: 'flex',
  flexDirection: 'row',
  overflow: 'hidden',
};

const globePanelStyle: React.CSSProperties = {
  position: 'relative',
  overflow: 'hidden',
  minWidth: 0,
};

const optionsPanelStyle: React.CSSProperties = {
  overflow: 'auto',
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
};

const splitterStyle: React.CSSProperties = {
  width: '8px',
  flexShrink: 0,
  cursor: 'col-resize',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: PALETTE.DARK,
  borderLeft: `1px solid ${PALETTE.BORDER}`,
  borderRight: `1px solid ${PALETTE.BORDER}`,
  zIndex: 5,
};

const splitterLineStyle: React.CSSProperties = {
  width: '2px',
  height: '40px',
  borderRadius: '1px',
  backgroundColor: PALETTE.DIM,
};

const undockBtnStyle: React.CSSProperties = {
  padding: '4px 14px',
  fontSize: '11px',
  fontWeight: 500,
  backgroundColor: 'transparent',
  color: PALETTE.ORANGE,
  border: `1px solid ${PALETTE.ORANGE}`,
  borderRadius: '3px',
  cursor: 'pointer',
  transition: 'all 0.15s',
  letterSpacing: '0.05em',
  fontFamily: "'JetBrains Mono', monospace",
};
